import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Thermometer,
  RotateCcw,
  Shield,
  AlertCircle,
  CheckCircle,
  Clock,
  Plus,
  Eye,
  Play,
  FileText,
  Loader2,
  Package,
  ThermometerSnowflake,
  ThermometerSun
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Recall Classes
const RECALL_CLASSES = [
  { value: 'class_i', label: 'Class I - Dangerous', color: 'red' },
  { value: 'class_ii', label: 'Class II - Moderate Risk', color: 'yellow' },
  { value: 'class_iii', label: 'Class III - Minor Risk', color: 'blue' },
];

// Temperature Zones
const TEMP_ZONES = [
  { value: 'room_temperature', label: 'Room Temperature (15-25°C)', range: '15-25°C' },
  { value: 'refrigerated', label: 'Refrigerated (2-8°C)', range: '2-8°C' },
  { value: 'freezer', label: 'Freezer (-25 to -10°C)', range: '-25 to -10°C' },
  { value: 'controlled_room', label: 'Controlled Room (20-25°C)', range: '20-25°C' },
];

// Return Sources
const RETURN_SOURCES = [
  { value: 'patient', label: 'Patient' },
  { value: 'specialist', label: 'Specialist' },
  { value: 'pharmacy', label: 'Pharmacy' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'internal', label: 'Internal' },
];

// Return Reasons
const RETURN_REASONS = [
  { value: 'unused', label: 'Unused' },
  { value: 'wrong_medication', label: 'Wrong Medication' },
  { value: 'wrong_dosage', label: 'Wrong Dosage' },
  { value: 'adverse_reaction', label: 'Adverse Reaction' },
  { value: 'expired', label: 'Expired' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'recalled', label: 'Recalled' },
  { value: 'patient_deceased', label: 'Patient Deceased' },
  { value: 'treatment_changed', label: 'Treatment Changed' },
  { value: 'other', label: 'Other' },
];

// Return Conditions
const RETURN_CONDITIONS = [
  { value: 'good', label: 'Good - Can Restock' },
  { value: 'opened', label: 'Opened' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'expired', label: 'Expired' },
  { value: 'contaminated', label: 'Contaminated' },
  { value: 'unknown', label: 'Unknown' },
];

// Return Actions
const RETURN_ACTIONS = [
  { value: 'restock', label: 'Restock' },
  { value: 'quarantine', label: 'Quarantine' },
  { value: 'dispose', label: 'Dispose' },
  { value: 'return_to_supplier', label: 'Return to Supplier' },
];

export const CompliancePage = () => {
  const { t } = useTranslation('compliance');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Recalls
  const [recalls, setRecalls] = useState([]);
  const [showRecallForm, setShowRecallForm] = useState(false);
  const [medications, setMedications] = useState([]);
  
  // Temperature
  const [tempLogs, setTempLogs] = useState([]);
  const [tempAlerts, setTempAlerts] = useState([]);
  const [showTempForm, setShowTempForm] = useState(false);
  
  // Returns
  const [returns, setReturns] = useState([]);
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [showProcessDialog, setShowProcessDialog] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/compliance/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, []);

  const fetchRecalls = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/compliance/recalls`);
      setRecalls(response.data.recalls);
    } catch (error) {
      console.error('Error fetching recalls:', error);
    }
  }, []);

  const fetchTempLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/compliance/temperature/logs?limit=100`);
      setTempLogs(response.data.logs);
    } catch (error) {
      console.error('Error fetching temp logs:', error);
    }
  }, []);

  const fetchTempAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/compliance/temperature/alerts`);
      setTempAlerts(response.data.alerts);
    } catch (error) {
      console.error('Error fetching temp alerts:', error);
    }
  }, []);

  const fetchReturns = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/compliance/returns`);
      setReturns(response.data.returns);
    } catch (error) {
      console.error('Error fetching returns:', error);
    }
  }, []);

  const fetchMedications = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/medications/catalog`);
      setMedications(response.data);
    } catch (error) {
      console.error('Error fetching medications:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchMedications()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchMedications]);

  useEffect(() => {
    if (activeTab === 'recalls') fetchRecalls();
    if (activeTab === 'temperature') {
      fetchTempLogs();
      fetchTempAlerts();
    }
    if (activeTab === 'returns') fetchReturns();
  }, [activeTab, fetchRecalls, fetchTempLogs, fetchTempAlerts, fetchReturns]);

  // Dashboard Component
  const DashboardTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card className="border-l-4 border-l-red-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t('dashboard.activeRecalls')}</p>
              <p className="text-3xl font-bold text-red-600">{dashboard?.recalls?.active || 0}</p>
            </div>
            <AlertTriangle className="h-10 w-10 text-red-500 opacity-50" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-l-4 border-l-orange-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t('dashboard.tempAlerts')}</p>
              <p className="text-3xl font-bold text-orange-600">{dashboard?.temperature?.unacknowledged_alerts || 0}</p>
              <p className="text-xs text-red-500">{dashboard?.temperature?.critical_alerts || 0} {t('dashboard.criticalAlerts')}</p>
            </div>
            <Thermometer className="h-10 w-10 text-orange-500 opacity-50" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-l-4 border-l-yellow-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t('dashboard.pendingReturns')}</p>
              <p className="text-3xl font-bold text-yellow-600">{dashboard?.returns?.pending || 0}</p>
            </div>
            <RotateCcw className="h-10 w-10 text-yellow-500 opacity-50" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-l-4 border-l-purple-500">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t('dashboard.quarantined')}</p>
              <p className="text-3xl font-bold text-purple-600">{dashboard?.quarantine?.items || 0}</p>
            </div>
            <Shield className="h-10 w-10 text-purple-500 opacity-50" />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Recalls Tab Component
  const RecallsTab = () => {
    const [recallForm, setRecallForm] = useState({
      medication_id: '',
      medication_name: '',
      batch_numbers: [],
      recall_class: 'class_ii',
      reason: '',
      description: '',
      regulatory_reference: '',
      action_required: 'Remove from inventory and quarantine',
      deadline: ''
    });
    const [saving, setSaving] = useState(false);

    const handleCreateRecall = async () => {
      if (!recallForm.medication_id || !recallForm.reason || recallForm.batch_numbers.length === 0) {
        toast.error('Please fill required fields');
        return;
      }

      setSaving(true);
      try {
        await axios.post(`${API_URL}/api/compliance/recalls`, recallForm);
        toast.success(t('recalls.messages.created'));
        setShowRecallForm(false);
        fetchRecalls();
        fetchDashboard();
      } catch (error) {
        toast.error(t('recalls.messages.error'));
      }
      setSaving(false);
    };

    const getClassBadge = (recallClass) => {
      const classInfo = RECALL_CLASSES.find(c => c.value === recallClass);
      const colors = {
        red: 'bg-red-100 text-red-800',
        yellow: 'bg-yellow-100 text-yellow-800',
        blue: 'bg-blue-100 text-blue-800'
      };
      return <Badge className={colors[classInfo?.color || 'blue']}>{classInfo?.label || recallClass}</Badge>;
    };

    const getStatusBadge = (status) => {
      const colors = {
        initiated: 'bg-blue-100 text-blue-800',
        in_progress: 'bg-yellow-100 text-yellow-800',
        completed: 'bg-green-100 text-green-800',
        cancelled: 'bg-gray-100 text-gray-800'
      };
      return <Badge className={colors[status] || 'bg-gray-100'}>{t(`recalls.statuses.${status}`)}</Badge>;
    };

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">{t('recalls.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('recalls.subtitle')}</p>
          </div>
          <Button onClick={() => setShowRecallForm(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="h-4 w-4 mr-2" />
            {t('recalls.newRecall')}
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-3 text-left text-sm font-medium">{t('recalls.recallNumber')}</th>
                  <th className="p-3 text-left text-sm font-medium">{t('recalls.medication')}</th>
                  <th className="p-3 text-left text-sm font-medium">{t('recalls.class')}</th>
                  <th className="p-3 text-left text-sm font-medium">{t('recalls.reason')}</th>
                  <th className="p-3 text-left text-sm font-medium">{t('recalls.status')}</th>
                  <th className="p-3 text-right text-sm font-medium">{t('recalls.affected')}</th>
                  <th className="p-3 text-right text-sm font-medium">{t('recalls.recalled')}</th>
                </tr>
              </thead>
              <tbody>
                {recalls.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-muted-foreground">
                      {t('recalls.noRecalls')}
                    </td>
                  </tr>
                ) : (
                  recalls.map((recall) => (
                    <tr key={recall.id} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-mono text-sm">{recall.recall_number}</td>
                      <td className="p-3">{recall.medication_name}</td>
                      <td className="p-3">{getClassBadge(recall.recall_class)}</td>
                      <td className="p-3 max-w-xs truncate">{recall.reason}</td>
                      <td className="p-3">{getStatusBadge(recall.status)}</td>
                      <td className="p-3 text-right">{recall.affected_quantity}</td>
                      <td className="p-3 text-right font-semibold">{recall.recalled_quantity}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Create Recall Dialog */}
        <Dialog open={showRecallForm} onOpenChange={setShowRecallForm}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-5 w-5" />
                {t('recalls.form.title')}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>{t('recalls.form.selectMedication')} *</Label>
                <Select 
                  value={recallForm.medication_id} 
                  onValueChange={(val) => {
                    const med = medications.find(m => m.id === val);
                    setRecallForm(prev => ({
                      ...prev,
                      medication_id: val,
                      medication_name: med?.generic_name || ''
                    }));
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {medications.map(med => (
                      <SelectItem key={med.id} value={med.id}>
                        {med.generic_name} - {med.concentration}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('recalls.form.selectBatches')} *</Label>
                <Input 
                  placeholder="Enter batch numbers separated by comma"
                  onChange={(e) => setRecallForm(prev => ({
                    ...prev,
                    batch_numbers: e.target.value.split(',').map(b => b.trim()).filter(Boolean)
                  }))}
                />
              </div>

              <div>
                <Label>{t('recalls.form.recallClass')} *</Label>
                <Select 
                  value={recallForm.recall_class} 
                  onValueChange={(val) => setRecallForm(prev => ({...prev, recall_class: val}))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RECALL_CLASSES.map(cls => (
                      <SelectItem key={cls.value} value={cls.value}>{cls.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('recalls.form.reason')} *</Label>
                <Textarea 
                  value={recallForm.reason}
                  onChange={(e) => setRecallForm(prev => ({...prev, reason: e.target.value}))}
                  placeholder={t('recalls.form.reasonPlaceholder')}
                />
              </div>

              <div>
                <Label>{t('recalls.form.regulatoryRef')}</Label>
                <Input 
                  value={recallForm.regulatory_reference}
                  onChange={(e) => setRecallForm(prev => ({...prev, regulatory_reference: e.target.value}))}
                />
              </div>

              <div>
                <Label>{t('recalls.form.deadline')}</Label>
                <Input 
                  type="date"
                  value={recallForm.deadline}
                  onChange={(e) => setRecallForm(prev => ({...prev, deadline: e.target.value}))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRecallForm(false)}>
                {t('recalls.form.cancel')}
              </Button>
              <Button onClick={handleCreateRecall} disabled={saving} className="bg-red-600 hover:bg-red-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
                {t('recalls.form.submit')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // Temperature Tab Component
  const TemperatureTab = () => {
    const [tempForm, setTempForm] = useState({
      zone: 'refrigerated',
      location_name: '',
      temperature_celsius: '',
      humidity_percent: '',
      notes: ''
    });
    const [saving, setSaving] = useState(false);

    const handleLogTemp = async () => {
      if (!tempForm.location_name || !tempForm.temperature_celsius) {
        toast.error('Please fill required fields');
        return;
      }

      setSaving(true);
      try {
        await axios.post(`${API_URL}/api/compliance/temperature/log`, {
          ...tempForm,
          temperature_celsius: parseFloat(tempForm.temperature_celsius),
          humidity_percent: tempForm.humidity_percent ? parseFloat(tempForm.humidity_percent) : null
        });
        toast.success(t('temperature.messages.logged'));
        setShowTempForm(false);
        fetchTempLogs();
        fetchTempAlerts();
        fetchDashboard();
        setTempForm({
          zone: 'refrigerated',
          location_name: '',
          temperature_celsius: '',
          humidity_percent: '',
          notes: ''
        });
      } catch (error) {
        toast.error(t('temperature.messages.error'));
      }
      setSaving(false);
    };

    const handleAcknowledgeAlert = async (alertId) => {
      try {
        await axios.put(`${API_URL}/api/compliance/temperature/alerts/${alertId}/acknowledge`);
        toast.success(t('temperature.messages.alertAcknowledged'));
        fetchTempAlerts();
        fetchDashboard();
      } catch (error) {
        toast.error('Error acknowledging alert');
      }
    };

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">{t('temperature.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('temperature.subtitle')}</p>
          </div>
          <Button onClick={() => setShowTempForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('temperature.logTemp')}
          </Button>
        </div>

        {/* Alerts Section */}
        {tempAlerts.length > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-orange-800 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                {t('temperature.alerts.title')} ({tempAlerts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {tempAlerts.slice(0, 5).map(alert => (
                  <div key={alert.id} className="flex items-center justify-between bg-white p-3 rounded border">
                    <div className="flex items-center gap-3">
                      {alert.alert_type === 'high_temp' ? 
                        <ThermometerSun className="h-5 w-5 text-red-500" /> : 
                        <ThermometerSnowflake className="h-5 w-5 text-blue-500" />
                      }
                      <div>
                        <p className="font-medium">{alert.location_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {alert.temperature_recorded}°C (Expected: {alert.expected_range})
                        </p>
                      </div>
                      <Badge className={alert.severity === 'critical' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                        {alert.severity}
                      </Badge>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleAcknowledgeAlert(alert.id)}>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      {t('temperature.alerts.acknowledge')}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logs Table */}
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-3 text-left text-sm font-medium">{t('temperature.table.datetime')}</th>
                  <th className="p-3 text-left text-sm font-medium">{t('temperature.table.location')}</th>
                  <th className="p-3 text-right text-sm font-medium">{t('temperature.table.temperature')}</th>
                  <th className="p-3 text-right text-sm font-medium">{t('temperature.table.humidity')}</th>
                  <th className="p-3 text-center text-sm font-medium">{t('temperature.table.status')}</th>
                  <th className="p-3 text-left text-sm font-medium">{t('temperature.table.recordedBy')}</th>
                </tr>
              </thead>
              <tbody>
                {tempLogs.map((log) => (
                  <tr key={log.id} className="border-t hover:bg-muted/30">
                    <td className="p-3 text-sm">
                      {new Date(log.recorded_at).toLocaleString()}
                    </td>
                    <td className="p-3">{log.location_name}</td>
                    <td className="p-3 text-right font-mono">
                      {log.temperature_celsius}°C / {log.temperature_fahrenheit}°F
                    </td>
                    <td className="p-3 text-right">
                      {log.humidity_percent ? `${log.humidity_percent}%` : '-'}
                    </td>
                    <td className="p-3 text-center">
                      {log.is_within_range ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {t('temperature.status.inRange')}
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {t('temperature.status.outOfRange')}
                        </Badge>
                      )}
                    </td>
                    <td className="p-3 text-sm">{log.recorded_by_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Log Temperature Dialog */}
        <Dialog open={showTempForm} onOpenChange={setShowTempForm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Thermometer className="h-5 w-5 text-blue-500" />
                {t('temperature.form.title')}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>{t('temperature.form.zone')} *</Label>
                <Select 
                  value={tempForm.zone} 
                  onValueChange={(val) => setTempForm(prev => ({...prev, zone: val}))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TEMP_ZONES.map(zone => (
                      <SelectItem key={zone.value} value={zone.value}>{zone.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('temperature.form.location')} *</Label>
                <Input 
                  value={tempForm.location_name}
                  onChange={(e) => setTempForm(prev => ({...prev, location_name: e.target.value}))}
                  placeholder={t('temperature.form.locationPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('temperature.form.temperature')} *</Label>
                  <Input 
                    type="number"
                    step="0.1"
                    value={tempForm.temperature_celsius}
                    onChange={(e) => setTempForm(prev => ({...prev, temperature_celsius: e.target.value}))}
                  />
                </div>
                <div>
                  <Label>{t('temperature.form.humidity')}</Label>
                  <Input 
                    type="number"
                    value={tempForm.humidity_percent}
                    onChange={(e) => setTempForm(prev => ({...prev, humidity_percent: e.target.value}))}
                  />
                </div>
              </div>

              <div>
                <Label>{t('temperature.form.notes')}</Label>
                <Textarea 
                  value={tempForm.notes}
                  onChange={(e) => setTempForm(prev => ({...prev, notes: e.target.value}))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTempForm(false)}>Cancel</Button>
              <Button onClick={handleLogTemp} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {t('temperature.form.submit')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // Returns Tab Component
  const ReturnsTab = () => {
    const [returnForm, setReturnForm] = useState({
      medication_id: '',
      medication_name: '',
      batch_number: '',
      quantity: 1,
      unit: 'unidad',
      source: 'patient',
      source_name: '',
      reason: 'unused',
      reason_detail: '',
      condition: 'unknown',
      notes: ''
    });
    const [processForm, setProcessForm] = useState({
      condition: 'good',
      action: 'restock',
      restocked_quantity: 0,
      disposed_quantity: 0,
      disposal_method: '',
      action_notes: ''
    });
    const [saving, setSaving] = useState(false);

    const handleCreateReturn = async () => {
      if (!returnForm.medication_id || !returnForm.quantity) {
        toast.error('Please fill required fields');
        return;
      }

      setSaving(true);
      try {
        await axios.post(`${API_URL}/api/compliance/returns`, returnForm);
        toast.success(t('returns.messages.created'));
        setShowReturnForm(false);
        fetchReturns();
        fetchDashboard();
      } catch (error) {
        toast.error(t('returns.messages.error'));
      }
      setSaving(false);
    };

    const handleProcessReturn = async () => {
      if (!selectedReturn) return;

      setSaving(true);
      try {
        await axios.put(`${API_URL}/api/compliance/returns/${selectedReturn.id}/process`, processForm);
        toast.success(t('returns.messages.processed'));
        setShowProcessDialog(false);
        setSelectedReturn(null);
        fetchReturns();
        fetchDashboard();
      } catch (error) {
        toast.error(t('returns.messages.error'));
      }
      setSaving(false);
    };

    const getStatusBadge = (status) => {
      const colors = {
        received: 'bg-blue-100 text-blue-800',
        inspecting: 'bg-yellow-100 text-yellow-800',
        processed: 'bg-purple-100 text-purple-800',
        completed: 'bg-green-100 text-green-800'
      };
      return <Badge className={colors[status] || 'bg-gray-100'}>{t(`returns.statuses.${status}`)}</Badge>;
    };

    const getActionBadge = (action) => {
      const colors = {
        pending: 'bg-gray-100 text-gray-800',
        restock: 'bg-green-100 text-green-800',
        quarantine: 'bg-purple-100 text-purple-800',
        dispose: 'bg-red-100 text-red-800',
        return_to_supplier: 'bg-blue-100 text-blue-800'
      };
      return <Badge className={colors[action] || 'bg-gray-100'}>{t(`returns.actionTypes.${action}`)}</Badge>;
    };

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">{t('returns.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('returns.subtitle')}</p>
          </div>
          <Button onClick={() => setShowReturnForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('returns.newReturn')}
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-3 text-left text-sm font-medium">{t('returns.returnNumber')}</th>
                  <th className="p-3 text-left text-sm font-medium">{t('returns.medication')}</th>
                  <th className="p-3 text-right text-sm font-medium">{t('returns.quantity')}</th>
                  <th className="p-3 text-left text-sm font-medium">{t('returns.source')}</th>
                  <th className="p-3 text-left text-sm font-medium">{t('returns.reason')}</th>
                  <th className="p-3 text-center text-sm font-medium">{t('returns.action')}</th>
                  <th className="p-3 text-center text-sm font-medium">{t('returns.status')}</th>
                  <th className="p-3 text-center text-sm font-medium">{t('returns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {returns.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted-foreground">
                      {t('returns.noReturns')}
                    </td>
                  </tr>
                ) : (
                  returns.map((ret) => (
                    <tr key={ret.id} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-mono text-sm">{ret.return_number}</td>
                      <td className="p-3">{ret.medication_name}</td>
                      <td className="p-3 text-right">{ret.quantity} {ret.unit}</td>
                      <td className="p-3">{t(`returns.sources.${ret.source}`)}</td>
                      <td className="p-3">{t(`returns.reasons.${ret.reason}`)}</td>
                      <td className="p-3 text-center">{getActionBadge(ret.action)}</td>
                      <td className="p-3 text-center">{getStatusBadge(ret.status)}</td>
                      <td className="p-3 text-center">
                        {ret.status === 'received' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setSelectedReturn(ret);
                              setProcessForm(prev => ({
                                ...prev,
                                restocked_quantity: ret.quantity,
                                disposed_quantity: 0
                              }));
                              setShowProcessDialog(true);
                            }}
                          >
                            <Play className="h-4 w-4 mr-1" />
                            Process
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Create Return Dialog */}
        <Dialog open={showReturnForm} onOpenChange={setShowReturnForm}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-yellow-500" />
                {t('returns.form.title')}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>{t('returns.form.selectMedication')} *</Label>
                <Select 
                  value={returnForm.medication_id} 
                  onValueChange={(val) => {
                    const med = medications.find(m => m.id === val);
                    setReturnForm(prev => ({
                      ...prev,
                      medication_id: val,
                      medication_name: med?.generic_name || ''
                    }));
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {medications.map(med => (
                      <SelectItem key={med.id} value={med.id}>
                        {med.generic_name} - {med.concentration}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('returns.form.quantity')} *</Label>
                  <Input 
                    type="number"
                    min="1"
                    value={returnForm.quantity}
                    onChange={(e) => setReturnForm(prev => ({...prev, quantity: parseInt(e.target.value)}))}
                  />
                </div>
                <div>
                  <Label>{t('returns.form.batchNumber')}</Label>
                  <Input 
                    value={returnForm.batch_number}
                    onChange={(e) => setReturnForm(prev => ({...prev, batch_number: e.target.value}))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('returns.form.source')} *</Label>
                  <Select 
                    value={returnForm.source} 
                    onValueChange={(val) => setReturnForm(prev => ({...prev, source: val}))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {RETURN_SOURCES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('returns.form.sourceName')}</Label>
                  <Input 
                    value={returnForm.source_name}
                    onChange={(e) => setReturnForm(prev => ({...prev, source_name: e.target.value}))}
                  />
                </div>
              </div>

              <div>
                <Label>{t('returns.form.reason')} *</Label>
                <Select 
                  value={returnForm.reason} 
                  onValueChange={(val) => setReturnForm(prev => ({...prev, reason: val}))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RETURN_REASONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('returns.form.condition')}</Label>
                <Select 
                  value={returnForm.condition} 
                  onValueChange={(val) => setReturnForm(prev => ({...prev, condition: val}))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RETURN_CONDITIONS.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('returns.form.notes')}</Label>
                <Textarea 
                  value={returnForm.notes}
                  onChange={(e) => setReturnForm(prev => ({...prev, notes: e.target.value}))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReturnForm(false)}>Cancel</Button>
              <Button onClick={handleCreateReturn} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {t('returns.form.submit')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Process Return Dialog */}
        <Dialog open={showProcessDialog} onOpenChange={setShowProcessDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('returns.process.title')}</DialogTitle>
              <DialogDescription>
                {selectedReturn?.return_number} - {selectedReturn?.medication_name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>{t('returns.process.assessCondition')}</Label>
                <Select 
                  value={processForm.condition} 
                  onValueChange={(val) => setProcessForm(prev => ({...prev, condition: val}))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RETURN_CONDITIONS.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('returns.process.determineAction')}</Label>
                <Select 
                  value={processForm.action} 
                  onValueChange={(val) => setProcessForm(prev => ({...prev, action: val}))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RETURN_ACTIONS.map(a => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {processForm.action === 'restock' && (
                <div>
                  <Label>{t('returns.process.restockQty')}</Label>
                  <Input 
                    type="number"
                    max={selectedReturn?.quantity || 0}
                    value={processForm.restocked_quantity}
                    onChange={(e) => setProcessForm(prev => ({...prev, restocked_quantity: parseInt(e.target.value)}))}
                  />
                </div>
              )}

              {processForm.action === 'dispose' && (
                <>
                  <div>
                    <Label>{t('returns.process.disposeQty')}</Label>
                    <Input 
                      type="number"
                      max={selectedReturn?.quantity || 0}
                      value={processForm.disposed_quantity}
                      onChange={(e) => setProcessForm(prev => ({...prev, disposed_quantity: parseInt(e.target.value)}))}
                    />
                  </div>
                  <div>
                    <Label>{t('returns.process.disposalMethod')}</Label>
                    <Input 
                      value={processForm.disposal_method}
                      onChange={(e) => setProcessForm(prev => ({...prev, disposal_method: e.target.value}))}
                    />
                  </div>
                </>
              )}

              <div>
                <Label>{t('returns.process.actionNotes')}</Label>
                <Textarea 
                  value={processForm.action_notes}
                  onChange={(e) => setProcessForm(prev => ({...prev, action_notes: e.target.value}))}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowProcessDialog(false)}>Cancel</Button>
              <Button onClick={handleProcessReturn} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {t('returns.process.submit')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[var(--line)] border-t-[var(--red)] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-2 space-y-6" data-testid="compliance-page" style={{ fontFamily: "'Inter Tight', system-ui, sans-serif" }}>
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-[var(--ink)]" style={{ letterSpacing: '-0.02em' }}>{t('title')}</h1>
        <p className="text-[var(--ink-3)] mt-1 text-sm">{t('subtitle')}</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="dashboard">
            <Shield className="h-4 w-4" />
            {t('tabs.dashboard')}
          </TabsTrigger>
          <TabsTrigger value="recalls">
            <AlertTriangle className="h-4 w-4" />
            {t('tabs.recalls')}
            {dashboard?.recalls?.active > 0 && (
              <Badge className="bg-[var(--red)] text-white ml-1 text-[10px] px-1.5 py-0.5">{dashboard.recalls.active}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="temperature">
            <Thermometer className="h-4 w-4" />
            {t('tabs.temperature')}
            {dashboard?.temperature?.unacknowledged_alerts > 0 && (
              <Badge className="bg-[var(--red)] text-white ml-1 text-[10px] px-1.5 py-0.5">{dashboard.temperature.unacknowledged_alerts}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="returns">
            <RotateCcw className="h-4 w-4" />
            {t('tabs.returns')}
            {dashboard?.returns?.pending > 0 && (
              <Badge className="bg-[var(--red)] text-white ml-1 text-[10px] px-1.5 py-0.5">{dashboard.returns.pending}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          <DashboardTab />
        </TabsContent>

        <TabsContent value="recalls" className="mt-6">
          <RecallsTab />
        </TabsContent>

        <TabsContent value="temperature" className="mt-6">
          <TemperatureTab />
        </TabsContent>

        <TabsContent value="returns" className="mt-6">
          <ReturnsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CompliancePage;
